import React, { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/auth';
import { getApiUrl } from '../config/api';
import './AdminPanel.css';

function AdminPanel({ user }) {
  const isAdminRole = (role) => {
    if (!role) return false;
    const normalized = String(role).trim().toLowerCase();
    return normalized === 'admin' || normalized === 'role_admin';
  };

  const [activeTab, setActiveTab] = useState('overview');
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [consolidatedBookings, setConsolidatedBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [bookingStatusFilter, setBookingStatusFilter] = useState('ALL');
  
  // Modal states
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isBookingDetailModalOpen, setIsBookingDetailModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingFacility, setEditingFacility] = useState(null);
  const [editingPricing, setEditingPricing] = useState(null);  // Form states
  const [roomForm, setRoomForm] = useState({
    name: '',
    type: '',
    size: '',
    price: '',
    singleOccupancyPrice: '',
    doubleOccupancyPrice: '',
    extraBedPrice: '',
    breakfastPrice: '',
    maxGuests: '',
    description: '',
    available: true,
    totalRooms: '',
    availableRooms: '',
    facilities: [],
    imageUrl: ''
  });
  
  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    role: 'USER'
  });

  const [facilityForm, setFacilityForm] = useState({
    name: '',
    description: ''
  });
  
  const [pricingForm, setPricingForm] = useState({
    roomId: '',
    startDate: '',
    endDate: '',
    singleOccupancyPrice: '',
    doubleOccupancyPrice: '',
    availableRoomsOverride: '',
    notes: ''
  });
  
  const [bookingForm, setBookingForm] = useState({
    userId: '',
    roomId: '',
    checkInDate: '',
    checkOutDate: '',
    guests: 1,
    roomPrice: '',
    extraBeds: 0,
    includeBreakfast: false,
    // New user creation fields
    createNewUser: true,
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    existingUserEmail: ''
  });
  const [roomPriceAutoFilled, setRoomPriceAutoFilled] = useState(true);

  // Calendar / Pricing view state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarRoomFilter, setCalendarRoomFilter] = useState('ALL');
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [bulkRangeStart, setBulkRangeStart] = useState('');
  const [bulkRangeEnd, setBulkRangeEnd] = useState('');
  const [bulkUpdateForm, setBulkUpdateForm] = useState({
    notes: '',
    roomRows: {}
  });
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const getRoomSortPriority = (room) => {
    const roomText = `${room?.name || ''} ${room?.type || ''}`.toLowerCase();
    if (roomText.includes('super') && (roomText.includes('delux') || roomText.includes('deluxe'))) {
      return 1;
    }
    if (roomText.includes('delux') || roomText.includes('deluxe')) {
      return 0;
    }
    if (roomText.includes('premier') || roomText.includes('premium')) {
      return 2;
    }
    if (roomText.includes('executive')) {
      return 3;
    }
    return 99;
  };

  const sortRoomsByDisplayOrder = (roomsList) => {
    return [...roomsList].sort((left, right) => {
      const leftPriority = getRoomSortPriority(left);
      const rightPriority = getRoomSortPriority(right);

      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }

      return String(left?.name || '').localeCompare(String(right?.name || ''));
    });
  };

  const sortPricingRulesByRecency = (rulesList) => {
    return [...rulesList].sort((left, right) => {
      const leftCreatedAt = left.createdAt ? new Date(left.createdAt).getTime() : 0;
      const rightCreatedAt = right.createdAt ? new Date(right.createdAt).getTime() : 0;

      if (leftCreatedAt !== rightCreatedAt) {
        return rightCreatedAt - leftCreatedAt;
      }

      const leftId = Number(left.id) || 0;
      const rightId = Number(right.id) || 0;
      return rightId - leftId;
    });
  };

  const upsertPricingRules = (existingRules, incomingRules) => {
    const incomingList = Array.isArray(incomingRules)
      ? incomingRules.filter(Boolean)
      : [incomingRules].filter(Boolean);

    if (incomingList.length === 0) {
      return existingRules;
    }

    const incomingIds = new Set(
      incomingList
        .map(rule => String(rule.id))
        .filter(id => id && id !== 'undefined' && id !== 'null')
    );

    const filteredExisting = existingRules.filter(rule => !incomingIds.has(String(rule.id)));
    return sortPricingRulesByRecency([...filteredExisting, ...incomingList]);
  };

  const parseJsonResponseSafely = async (response) => {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (_error) {
      return null;
    }
  };

  const getPricingRuleSpanDays = (rule) => {
    if (!rule?.startDate || !rule?.endDate) {
      return Number.MAX_SAFE_INTEGER;
    }

    const startTime = new Date(rule.startDate).getTime();
    const endTime = new Date(rule.endDate).getTime();

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
      return Number.MAX_SAFE_INTEGER;
    }

    return Math.floor((endTime - startTime) / (24 * 60 * 60 * 1000)) + 1;
  };

  const comparePricingRulesBySpecificity = (left, right) => {
    const leftSpanDays = getPricingRuleSpanDays(left);
    const rightSpanDays = getPricingRuleSpanDays(right);

    if (leftSpanDays !== rightSpanDays) {
      return leftSpanDays - rightSpanDays;
    }

    const leftCreatedAt = left.createdAt ? new Date(left.createdAt).getTime() : 0;
    const rightCreatedAt = right.createdAt ? new Date(right.createdAt).getTime() : 0;

    if (leftCreatedAt !== rightCreatedAt) {
      return rightCreatedAt - leftCreatedAt;
    }

    const leftId = Number(left.id) || 0;
    const rightId = Number(right.id) || 0;
    return rightId - leftId;
  };

  const getLatestPricingRuleForRoomDate = useCallback((roomId, dateStr) => {
    const matchingRules = pricingRules.filter(rule =>
      String(rule.roomId) === String(roomId) &&
      dateStr >= rule.startDate &&
      dateStr <= rule.endDate
    );

    if (matchingRules.length === 0) {
      return null;
    }

    return [...matchingRules].sort(comparePricingRulesBySpecificity)[0];
  }, [pricingRules]);

  const getRoomDefaultValuesForDate = useCallback((room, dateStr) => {
    const activeRule = getLatestPricingRuleForRoomDate(room.id, dateStr);

    return {
      enabled: true,
      singleOccupancyPrice: String(activeRule?.singleOccupancyPrice ?? room.singleOccupancyPrice ?? room.price ?? ''),
      doubleOccupancyPrice: String(activeRule?.doubleOccupancyPrice ?? room.doubleOccupancyPrice ?? room.price ?? ''),
      availableRoomsOverride: activeRule?.availableRoomsOverride != null ? String(activeRule.availableRoomsOverride) : ''
    };
  }, [getLatestPricingRuleForRoomDate]);

  const buildBulkRowsForDate = useCallback((dateStr) => {
    const rows = {};
    rooms.forEach(room => {
      rows[String(room.id)] = getRoomDefaultValuesForDate(room, dateStr);
    });
    return rows;
  }, [rooms, getRoomDefaultValuesForDate]);

  // Helper function to format currency with 2 decimal places
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return Number(amount).toFixed(2);
  };

  const getSuggestedRoomPrice = useCallback((roomId, checkInDate, guests) => {
    if (!roomId) return null;

    const selectedRoom = rooms.find(room => String(room.id) === String(roomId));
    if (!selectedRoom) return null;

    const fallbackPrice = Number(
      guests > 1
        ? (selectedRoom.doubleOccupancyPrice ?? selectedRoom.price)
        : (selectedRoom.singleOccupancyPrice ?? selectedRoom.price)
    );

    if (!checkInDate || !pricingRules.length) {
      return Number.isFinite(fallbackPrice) ? fallbackPrice : null;
    }

    const checkIn = new Date(checkInDate);
    const matchedRule = pricingRules.find(rule => {
      if (String(rule.roomId) !== String(roomId)) return false;
      if (!rule.startDate || !rule.endDate) return false;
      const start = new Date(rule.startDate);
      const end = new Date(rule.endDate);
      return checkIn >= start && checkIn <= end;
    });

    if (!matchedRule) {
      return Number.isFinite(fallbackPrice) ? fallbackPrice : null;
    }

    const rulePrice = Number(
      guests > 1
        ? (matchedRule.doubleOccupancyPrice ?? matchedRule.singleOccupancyPrice)
        : (matchedRule.singleOccupancyPrice ?? matchedRule.doubleOccupancyPrice)
    );

    if (Number.isFinite(rulePrice) && rulePrice > 0) {
      return rulePrice;
    }

    return Number.isFinite(fallbackPrice) ? fallbackPrice : null;
  }, [rooms, pricingRules]);

  const calculateBookingNights = (checkInDate, checkOutDate) => {
    if (!checkInDate || !checkOutDate) return 0;
    const start = new Date(checkInDate);
    const end = new Date(checkOutDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return 0;
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.ceil((end - start) / msPerDay);
  };

  const getAdminBookingTotal = () => {
    const roomPrice = Number(bookingForm.roomPrice);
    const nights = calculateBookingNights(bookingForm.checkInDate, bookingForm.checkOutDate);
    if (!Number.isFinite(roomPrice) || roomPrice <= 0 || nights <= 0) return 0;
    return roomPrice * nights;
  };

  useEffect(() => {
    // Check if user is logged in and is admin
    console.log('Admin Panel - Received user prop:', user);
    console.log('Admin Panel - User role:', user?.role);
    
    const isAdmin = user && isAdminRole(user.role);
    if (!user || !isAdmin) {
      console.error('Admin Panel - Access denied. Details:');
      console.error('User exists:', !!user);
      console.error('User ID exists:', !!user?.id);
      console.error('User role:', user?.role);
      console.error('Expected role: admin/ADMIN/ROLE_ADMIN');
      console.error('Is admin check:', isAdmin);
      
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    
    console.log('✅ ADMIN ACCESS GRANTED - Fetching admin data...');
    fetchAdminData();
  }, [user]);
  
  const fetchAdminData = async () => {
    console.log('AdminPanel - Starting fetchAdminData...');
    setLoading(true);
    try {
      // Helper function to safely parse JSON response
      const safeJsonParse = async (response) => {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error('Failed to parse JSON response:', text.substring(0, 200));
          throw new Error('Invalid JSON response from server');
        }
      };

      // Fetch rooms from the general rooms endpoint (not admin-specific)
      try {
        const roomsRes = await fetchWithAuth('/api/rooms', { suppressAutoLogout: true });
        if (roomsRes.ok) {
          const roomsData = await safeJsonParse(roomsRes);
          const sortedRooms = sortRoomsByDisplayOrder(Array.isArray(roomsData) ? roomsData : []);
          setRooms(sortedRooms);
          console.log('Rooms loaded:', sortedRooms.length);
          console.log('Sample room data:', sortedRooms[0]); // Debug: Check room structure
        } else {
          console.error('Failed to fetch rooms:', roomsRes.status);
          setRooms([]);
        }
      } catch (e) {
        console.log('Rooms endpoint error:', e.message);
        setRooms([]);
      }
      
      // Try to fetch facilities from admin endpoint
      try {
        const facilitiesRes = await fetchWithAuth('/api/admin/facilities', { suppressAutoLogout: true });
        if (facilitiesRes.ok) {
          const facilitiesData = await safeJsonParse(facilitiesRes);
          setFacilities(Array.isArray(facilitiesData) ? facilitiesData : []);
          console.log('Facilities loaded:', Array.isArray(facilitiesData) ? facilitiesData.length : 0);
          console.log('Sample facility data:', facilitiesData[0]); // Debug: Check facility structure
        } else {
          console.error('Failed to fetch facilities:', facilitiesRes.status);
          setFacilities([]);
        }
      } catch (e) {
        console.log('Facilities endpoint not available:', e.message);
        setFacilities([]); // Set empty array if endpoint doesn't exist
      }

      // Try to fetch bookings from general endpoint
      try {
        const bookingsRes = await fetchWithAuth('/api/bookings', { suppressAutoLogout: true });
        if (bookingsRes.ok) {
          const bookingsData = await safeJsonParse(bookingsRes);
          setBookings(Array.isArray(bookingsData) ? bookingsData : []);
          console.log('Bookings loaded:', Array.isArray(bookingsData) ? bookingsData.length : 0);
          console.log('Sample booking data:', bookingsData[0]); // Debug: Check booking structure
        } else {
          console.log('Bookings endpoint returned:', bookingsRes.status);
          setBookings([]); // Set empty array if endpoint doesn't work
        }
      } catch (e) {
        console.log('Bookings endpoint not available:', e.message);
        setBookings([]);
      }

      // Fetch users from secured admin endpoint
      try {
        const usersRes = await fetchWithAuth('/api/admin/users', { suppressAutoLogout: true });
        if (usersRes.ok) {
          const usersData = await safeJsonParse(usersRes);
          setUsers(Array.isArray(usersData) ? usersData : []);
          console.log('Users loaded:', Array.isArray(usersData) ? usersData.length : 0);
        } else {
          console.log('Users endpoint returned:', usersRes.status);
          setUsers([]);
        }
      } catch (e) {
        console.log('Users endpoint not available:', e.message);
        setUsers([]);
      }


      // Try to fetch consolidated bookings
      try {
        const consolidatedRes = await fetchWithAuth('/api/admin/bookings/consolidated', { suppressAutoLogout: true });
        if (consolidatedRes.ok) {
          const consolidatedData = await safeJsonParse(consolidatedRes);
          setConsolidatedBookings(Array.isArray(consolidatedData) ? consolidatedData : []);
          console.log('Consolidated bookings loaded:', Array.isArray(consolidatedData) ? consolidatedData.length : 0);
        } else {
          console.log('Consolidated bookings endpoint returned:', consolidatedRes.status);
          setConsolidatedBookings([]);
        }
      } catch (e) {
        console.log('Consolidated bookings endpoint not available:', e.message);
        setConsolidatedBookings([]);
      }

      // Try to fetch pricing rules
      try {
        const pricingRes = await fetchWithAuth('/api/admin/pricing/list', { suppressAutoLogout: true });
        if (pricingRes.ok) {
          const pricingData = await safeJsonParse(pricingRes);
          // Handle both direct array and paginated response
          const pricingArray = Array.isArray(pricingData) ? pricingData : 
                              (pricingData.content && Array.isArray(pricingData.content)) ? pricingData.content : [];
          const sortedPricingArray = sortPricingRulesByRecency(pricingArray);
          setPricingRules(sortedPricingArray);
          console.log('Pricing rules loaded:', sortedPricingArray.length);
          console.log('Pricing rules data:', sortedPricingArray);
        } else {
          console.log('Pricing rules endpoint returned:', pricingRes.status);
          setPricingRules([]);
        }
      } catch (e) {
        console.log('Pricing rules endpoint not available:', e.message);
        setPricingRules([]);
      }
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
      setError('Failed to load some admin data. Some features may not be available.');
    } finally {
      setLoading(false);
    }
  };

  const cleanRoomDescription = (description) => {
    if (!description) return '';
    return description.trim();
  };

  const cleanRoomName = (name) => {
    if (!name) return '';
    return name.trim();
  };

  // Sorting function
  const sortData = (data, field) => {
    if (!field) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];
      
      // Handle nested fields for user/room names
      if (field === 'userName' && !aVal) {
        aVal = a.userName || a.userEmail || a.userId || '';
      }
      if (field === 'roomName' && !aVal) {
        aVal = a.roomName || a.roomId || '';
      }
      
      // Handle dates
      if (field.includes('Date')) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle strings and numbers
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Get sorted bookings
  const sortedBookings = sortData(bookings, sortBy);
  const filteredSortedBookings = sortedBookings.filter(booking =>
    bookingStatusFilter === 'ALL' ? true : booking.status === bookingStatusFilter
  );

  // Clean display helper
  const cleanDisplayText = (text, fallback = 'Unknown') => {
    if (!text) return fallback;
    return text.toString().replace(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i, fallback);
  };
  const handleEditRoom = (room) => {
    console.log('Editing room:', room); // Debug log to see room structure
    setEditingRoom(room);
    setRoomForm({
      name: cleanRoomName(room.name) || '',
      type: room.type || '',
      size: room.size || '',
      price: room.price || '',
      singleOccupancyPrice: room.singleOccupancyPrice || '',
      doubleOccupancyPrice: room.doubleOccupancyPrice || '',
      extraBedPrice: room.extraBedPrice || '',
      breakfastPrice: room.breakfastPrice || '',
      maxGuests: room.maxGuests || '',
      description: cleanRoomDescription(room.description) || '',
      available: room.available !== false,
      totalRooms: room.totalRooms || '',
      availableRooms: room.availableRooms || '',
      facilities: Array.isArray(room.facilities) ? room.facilities : [],
      imageUrl: room.imageUrl || ''
    });
    // Reset image selection when editing
    setSelectedImage(null);
    setImagePreview(room.imageUrl || null);
    setIsRoomModalOpen(true);
  };
  const handleAddRoom = () => {
    setEditingRoom(null);
    setRoomForm({
      name: '',
      type: '',
      size: '',
      price: '',
      singleOccupancyPrice: '',
      doubleOccupancyPrice: '',
      extraBedPrice: '',
      breakfastPrice: '',
      maxGuests: '',
      description: '',
      available: true,
      totalRooms: '',
      availableRooms: '',
      facilities: [],
      imageUrl: ''
    });
    // Reset image selection when adding new room
    setSelectedImage(null);
    setImagePreview(null);
    setIsRoomModalOpen(true);
  };

  const handleSaveRoom = async (e) => {
    e.preventDefault();      const roomData = {
      ...roomForm,
      price: parseFloat(roomForm.price),
      singleOccupancyPrice: roomForm.singleOccupancyPrice ? parseInt(roomForm.singleOccupancyPrice) : null,
      doubleOccupancyPrice: roomForm.doubleOccupancyPrice ? parseInt(roomForm.doubleOccupancyPrice) : null,
      extraBedPrice: roomForm.extraBedPrice ? parseInt(roomForm.extraBedPrice) : null,
      breakfastPrice: roomForm.breakfastPrice ? parseInt(roomForm.breakfastPrice) : null,
      maxGuests: parseInt(roomForm.maxGuests),
      totalRooms: roomForm.totalRooms ? parseInt(roomForm.totalRooms) : null,
      availableRooms: roomForm.availableRooms ? parseInt(roomForm.availableRooms) : null,
      facilities: roomForm.facilities
    };

    try {
      // Step 1: Create/Update room first
      const url = editingRoom ? `/api/admin/rooms/${editingRoom.id}` : '/api/admin/rooms';
      const method = editingRoom ? 'PUT' : 'POST';
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(roomData)
      });

      if (response.ok) {
        const savedRoom = await response.json();
        
        // Step 2: Upload image if selected
        if (selectedImage) {
          try {
            const imageUrl = await uploadImageToS3(savedRoom.id);
            
            // Step 3: Update room with image URL
            if (imageUrl) {
              await fetchWithAuth(`/api/admin/rooms/${savedRoom.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...savedRoom, imageUrl })
              });
            }
          } catch (imageError) {
            console.error('Image upload failed:', imageError);
            setError('Room saved but image upload failed. You can try uploading the image again by editing the room.');
          }
        }
        
        setSuccessMessage(editingRoom ? 'Room updated successfully' : 'Room added successfully');
        setIsRoomModalOpen(false);
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to save room');
      }
    } catch (error) {
      console.error('Error saving room:', error);
      setError('Failed to save room: ' + error.message);
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (!window.confirm('Are you sure you want to delete this room?')) return;

    try {
      const response = await fetchWithAuth(`/api/admin/rooms/${roomId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccessMessage('Room deleted successfully');
        fetchAdminData();
      } else {
        setError('Failed to delete room');
      }
    } catch (error) {
      console.error('Error deleting room:', error);
      setError('Failed to delete room');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username || user.name || '',
      email: user.email || '',
      role: user.role || 'USER'
    });
    setIsUserModalOpen(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetchWithAuth(`/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userForm)
      });

      if (response.ok) {
        setSuccessMessage('User updated successfully');
        setIsUserModalOpen(false);
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetchWithAuth(`/api/admin/users/${userId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccessMessage('User deleted successfully');
        fetchAdminData();
      } else {
        setError('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  // Facility Management Functions
  const handleAddFacility = () => {
    setEditingFacility(null);
    setFacilityForm({
      name: '',
      description: ''
    });
    setIsFacilityModalOpen(true);
  };

  const handleEditFacility = (facility) => {
    setEditingFacility(facility);
    setFacilityForm({
      name: facility.name || '',
      description: facility.description || ''
    });
    setIsFacilityModalOpen(true);
  };
  const handleSaveFacility = async (e) => {
    e.preventDefault();
    
    console.log('=== FACILITY SAVE DEBUG ===');
    console.log('Editing facility:', editingFacility);
    console.log('Facility form data:', facilityForm);
    console.log('Current user:', JSON.parse(localStorage.getItem('user') || '{}'));
    console.log('Current token exists:', !!localStorage.getItem('token'));
    
    try {
      const url = editingFacility ? `/api/admin/facilities/${editingFacility.id}` : '/api/admin/facilities';
      const method = editingFacility ? 'PUT' : 'POST';
      
      console.log('Request URL:', url);
      console.log('Request method:', method);
      console.log('Request body:', JSON.stringify(facilityForm));
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(facilityForm)
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData);
        
        setSuccessMessage(editingFacility ? 'Facility updated successfully' : 'Facility added successfully');
        setIsFacilityModalOpen(false);
        fetchAdminData();
      } else {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        
        setError(errorData || 'Failed to save facility');
      }
    } catch (error) {
      console.error('Exception in handleSaveFacility:', error);
      console.error('Error stack:', error.stack);
      setError('Failed to save facility: ' + error.message);
    }
  };

  const handleDeleteFacility = async (facilityId) => {
    if (!window.confirm('Are you sure you want to delete this facility?')) return;

    try {
      const response = await fetchWithAuth(`/api/admin/facilities/${facilityId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccessMessage('Facility deleted successfully');
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to delete facility');
      }
    } catch (error) {
      console.error('Error deleting facility:', error);
      setError('Failed to delete facility');
    }
  };

  // Pricing Management Functions
  const handleAddPricing = () => {
    setPricingForm({
      roomId: '',
      startDate: '',
      endDate: '',
      singleOccupancyPrice: '',
      doubleOccupancyPrice: '',
      availableRoomsOverride: '',
      notes: ''
    });
    setEditingPricing(null);
    setIsPricingModalOpen(true);
  };

  const handleEditPricing = (pricing) => {
    setPricingForm({
      roomId: pricing.roomId.toString(),
      startDate: pricing.startDate,
      endDate: pricing.endDate,
      singleOccupancyPrice: pricing.singleOccupancyPrice.toString(),
      doubleOccupancyPrice: pricing.doubleOccupancyPrice.toString(),
      availableRoomsOverride: pricing.availableRoomsOverride !== null && pricing.availableRoomsOverride !== undefined
        ? pricing.availableRoomsOverride.toString()
        : '',
      notes: pricing.notes || ''
    });
    setEditingPricing(pricing);
    setIsPricingModalOpen(true);
  };

  const handleSavePricing = async (e) => {
    e.preventDefault();

    setError('');
    try {
      const url = editingPricing 
        ? `/api/admin/pricing/${editingPricing.id}`
        : '/api/admin/pricing/create';
      
      const method = editingPricing ? 'PUT' : 'POST';
      const parsedOverride = pricingForm.availableRoomsOverride === ''
        ? null
        : Number.parseInt(pricingForm.availableRoomsOverride, 10);
      const payload = {
        ...pricingForm,
        availableRoomsOverride: Number.isNaN(parsedOverride) ? null : parsedOverride
      };
      
      const response = await fetchWithAuth(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const responseData = await parseJsonResponseSafely(response);
        const room = rooms.find(r => String(r.id) === String(payload.roomId));
        const optimisticRule = responseData && typeof responseData === 'object'
          ? responseData
          : {
              ...(editingPricing || {}),
              ...payload,
              id: editingPricing?.id ?? `temp-${Date.now()}-${payload.roomId}`,
              roomName: room?.name || editingPricing?.roomName || '',
              createdAt: editingPricing?.createdAt || new Date().toISOString()
            };

        setPricingRules(prev => upsertPricingRules(prev, optimisticRule));
        setSuccessMessage(editingPricing ? 'Pricing updated successfully' : 'Pricing created successfully');
        setIsPricingModalOpen(false);
        await fetchAdminData();
      } else {
        const errorText = await response.text();
        setError(errorText || 'Failed to save pricing');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      setError('Failed to save pricing');
    }
  };

  const handleDeletePricing = async (pricingId) => {
    if (!window.confirm('Are you sure you want to delete this pricing rule?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/pricing/${pricingId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccessMessage('Pricing rule deleted successfully');
        fetchAdminData();
      } else {
        setError('Failed to delete pricing rule');
      }
    } catch (error) {
      console.error('Error deleting pricing:', error);
      setError('Failed to delete pricing rule');
    }
  };

  const handleBulkSavePricing = async (e) => {
    e.preventDefault();
    if (!bulkRangeStart || !bulkRangeEnd) {
      setError('Please select both a start and end date for the bulk update.');
      return;
    }
    if (bulkRangeEnd < bulkRangeStart) {
      setError('End date must be on or after start date.');
      return;
    }

    setIsBulkSaving(true);
    setError('');
    try {
      const roomRows = bulkUpdateForm.roomRows || {};
      const targetRooms = rooms.filter(room => roomRows[String(room.id)]?.enabled);

      if (targetRooms.length === 0) {
        setError('Please enable at least one room row for bulk update.');
        return;
      }

      const promises = targetRooms.map(room => {
        const row = roomRows[String(room.id)] || {};
        const existingRule = pricingRules.find(r =>
          String(r.roomId) === String(room.id) &&
          r.startDate === bulkRangeStart &&
          r.endDate === bulkRangeEnd
        );
        const url = existingRule
          ? `/api/admin/pricing/${existingRule.id}`
          : '/api/admin/pricing/create';
        const method = existingRule ? 'PUT' : 'POST';

        const singlePrice = row.singleOccupancyPrice !== ''
          ? parseFloat(row.singleOccupancyPrice)
          : (room.singleOccupancyPrice || room.price || 0);
        const doublePrice = row.doubleOccupancyPrice !== ''
          ? parseFloat(row.doubleOccupancyPrice)
          : (room.doubleOccupancyPrice || room.price || 0);

        const parsedOverride = row.availableRoomsOverride === ''
          ? null
          : parseInt(row.availableRoomsOverride, 10);
        const safeOverride = Number.isNaN(parsedOverride) ? null : parsedOverride;

        const payload = {
          roomId: String(room.id),
          startDate: bulkRangeStart,
          endDate: bulkRangeEnd,
          singleOccupancyPrice: singlePrice,
          doubleOccupancyPrice: doublePrice,
          availableRoomsOverride: safeOverride,
          notes: bulkUpdateForm.notes
        };

        return fetchWithAuth(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(async response => {
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Failed to save rule for ${room.name}`);
          }

          const responseData = await parseJsonResponseSafely(response);
          const optimisticRule = responseData && typeof responseData === 'object'
            ? responseData
            : {
                ...(existingRule || {}),
                ...payload,
                id: existingRule?.id ?? `temp-${Date.now()}-${room.id}`,
                roomName: room.name,
                createdAt: existingRule?.createdAt || new Date().toISOString()
              };

          return optimisticRule;
        });
      });

      const savedOrUpdatedRules = await Promise.all(promises);
      setPricingRules(prev => upsertPricingRules(prev, savedOrUpdatedRules));
      setSuccessMessage(`Pricing/inventory rules saved for ${targetRooms.length} room(s) from ${bulkRangeStart} to ${bulkRangeEnd}.`);
      setBulkUpdateForm(prev => ({
        ...prev,
        notes: ''
      }));
      await fetchAdminData();
    } catch (err) {
      console.error('Bulk pricing save failed:', err);
      setError('Failed to save bulk pricing: ' + err.message);
    } finally {
      setIsBulkSaving(false);
    }
  };

  // Booking Management Functions
  const handleCreateBooking = () => {
    setBookingForm({
      userId: '',
      roomId: '',
      checkInDate: '',
      checkOutDate: '',
      guests: 1,
      roomPrice: '',
      extraBeds: 0,
      includeBreakfast: false,
      createNewUser: true,
      guestName: '',
      guestEmail: '',
      guestPhone: '',
      existingUserEmail: ''
    });
    setRoomPriceAutoFilled(true);
    setIsCreateBookingModalOpen(true);
  };

  const handleSaveBooking = async (e) => {
    e.preventDefault();
    
    console.log('=== ADMIN BOOKING CREATION ===');
    console.log('Booking form data:', bookingForm);
    
    try {
      const url = editingBooking ? `/api/admin/bookings/${editingBooking.id}` : '/api/admin/bookings/create-simple';
      const method = editingBooking ? 'PUT' : 'POST';
      
      // Prepare booking data for the working endpoint
      const bookingData = {
        roomId: bookingForm.roomId,
        checkInDate: bookingForm.checkInDate,
        checkOutDate: bookingForm.checkOutDate,
        guests: bookingForm.guests,
        extraBeds: bookingForm.extraBeds,
        includeBreakfast: bookingForm.includeBreakfast,
        userEmail: bookingForm.createNewUser ? bookingForm.guestEmail : bookingForm.existingUserEmail
      };

      const nights = calculateBookingNights(bookingForm.checkInDate, bookingForm.checkOutDate);
      const roomPrice = Number(bookingForm.roomPrice);
      if (Number.isFinite(roomPrice) && roomPrice > 0 && nights > 0) {
        bookingData.roomPrice = roomPrice;
        bookingData.totalPrice = Number((roomPrice * nights).toFixed(2));
      }
      
      // Add guest information if creating new user
      if (bookingForm.createNewUser) {
        bookingData.guestName = bookingForm.guestName;
        bookingData.guestPhone = bookingForm.guestPhone;
      }
      
      console.log('Sending booking data:', bookingData);
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Booking created successfully:', result);
        
        setSuccessMessage(
          editingBooking 
            ? 'Booking updated successfully' 
            : `Booking created successfully for ${bookingForm.createNewUser ? bookingForm.guestName : bookingForm.existingUserEmail}. ${bookingForm.guestPhone ? 'Confirmation SMS sent.' : ''}`
        );
        setIsCreateBookingModalOpen(false);
        setEditingBooking(null);
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || `Failed to ${editingBooking ? 'update' : 'create'} booking`);
      }
    } catch (error) {
      console.error(`Error ${editingBooking ? 'updating' : 'creating'} booking:`, error);
      setError(`Failed to ${editingBooking ? 'update' : 'create'} booking: ${error.message}`);
    }
  };

  const handleUpdateBookingStatus = async (booking, newStatus) => {
    if (!window.confirm(`Update booking #${booking.id} status to ${newStatus}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/bookings/${booking.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setSuccessMessage(`Booking status updated to ${newStatus} successfully`);
        fetchAdminData(); // Refresh the data to show updated room availability
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to update booking status');
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      setError('Failed to update booking status: ' + error.message);
    }
  };

  const closeModal = () => {
    setIsRoomModalOpen(false);
    setIsUserModalOpen(false);
    setIsFacilityModalOpen(false);
    setIsPricingModalOpen(false);
    setIsCreateBookingModalOpen(false);
    setIsBookingDetailModalOpen(false);
    setEditingRoom(null);
    setEditingUser(null);
    setEditingFacility(null);
    setEditingPricing(null);
    setEditingBooking(null);
    setSelectedBooking(null);
    setError('');
  };

  // Image handling functions
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToS3 = async (roomId) => {
    if (!selectedImage) return null;
    
    console.log('=== FRONTEND: Starting image upload ===');
    console.log('Room ID:', roomId);
    console.log('Selected image:', selectedImage?.name, selectedImage?.size, selectedImage?.type);
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('roomId', roomId);
      
      console.log('FormData created, uploading to:', '/api/admin/rooms/upload-image');
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
      }
      
      // Get token for authorization
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
        // Use direct fetch with manual authorization - avoid fetchWithAuth for file uploads
      const response = await fetch(getApiUrl('/api/admin/rooms/upload-image'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - let browser set it for multipart/form-data
        },
        body: formData
      });
      
      console.log('Upload response status:', response.status);
      console.log('Upload response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const result = await response.json();
        console.log('Upload successful, result:', result);
        return result.imageUrl;
      } else {
        const errorText = await response.text();
        console.error('Upload failed with status:', response.status, 'Error:', errorText);
        throw new Error(`Image upload failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  // Enhanced Booking Management Functions
  const handleRefreshBookings = async () => {
    setLoading(true);
    await fetchAdminData();
    setLoading(false);
  };

  const getBookingStatusColor = (status) => {
    const colors = {
      'CONFIRMED': '#28a745',
      'CHECKED_IN': '#007bff', 
      'CHECKED_OUT': '#6c757d',
      'CANCELLED': '#dc3545',
      'PENDING': '#ffc107',
      'PAYMENT_FAILED': '#fd7e14',
      'COMPLETED': '#28a745'
    };
    return colors[status] || '#6c757d';
  };

  const handleViewBooking = (booking) => {
    setSelectedBooking(booking);
    setIsBookingDetailModalOpen(true);
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking);
    const nights = calculateBookingNights(booking.checkInDate, booking.checkOutDate);
    const derivedRoomPrice = nights > 0 && booking.totalPrice ? Number(booking.totalPrice) / nights : '';
    setBookingForm({
      userId: booking.userId || '',
      roomId: booking.roomId || '',
      checkInDate: booking.checkInDate || '',
      checkOutDate: booking.checkOutDate || '',
      guests: booking.guests || 1,
      roomPrice: derivedRoomPrice ? Number(derivedRoomPrice).toFixed(2) : '',
      extraBeds: booking.extraBeds || 0,
      includeBreakfast: booking.breakfast || false,
      createNewUser: false,
      guestName: booking.userName || '',
      guestEmail: booking.userEmail || '',
      guestPhone: booking.userPhone || '',
      existingUserEmail: booking.userEmail || ''
    });
    setRoomPriceAutoFilled(false);
    setIsCreateBookingModalOpen(true);
  };

  useEffect(() => {
    if (!isCreateBookingModalOpen || !roomPriceAutoFilled || editingBooking) {
      return;
    }

    const suggestedPrice = getSuggestedRoomPrice(
      bookingForm.roomId,
      bookingForm.checkInDate,
      bookingForm.guests
    );

    if (suggestedPrice !== null && Number.isFinite(suggestedPrice)) {
      setBookingForm(prev => ({
        ...prev,
        roomPrice: Number(suggestedPrice).toFixed(2)
      }));
    }
  }, [
    isCreateBookingModalOpen,
    roomPriceAutoFilled,
    editingBooking,
    bookingForm.roomId,
    bookingForm.checkInDate,
    bookingForm.guests,
    getSuggestedRoomPrice
  ]);

  const handleCancelBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to cancel booking #${booking.id}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/bookings/${booking.id}/cancel`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sendNotification: true,
          reason: 'Cancelled by admin'
        })
      });

      if (response.ok) {
        setSuccessMessage('Booking cancelled successfully. Customer notifications triggered from backend.');
        
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to cancel booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      setError('Failed to cancel booking: ' + error.message);
    }
  };

  const handleCheckInBooking = async (booking) => {
    if (!window.confirm(`Check in booking #${booking.id} for ${booking.userName || booking.userEmail}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/bookings/${booking.id}/checkin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setSuccessMessage('Guest checked in successfully');
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to check in guest');
      }
    } catch (error) {
      console.error('Error checking in guest:', error);
      setError('Failed to check in guest: ' + error.message);
    }
  };

  const handleCheckOutBooking = async (booking) => {
    if (!window.confirm(`Check out booking #${booking.id} for ${booking.userName || booking.userEmail}?`)) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/bookings/${booking.id}/checkout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setSuccessMessage('Guest checked out successfully');
        fetchAdminData();
      } else {
        const errorData = await response.text();
        setError(errorData || 'Failed to check out guest');
      }
    } catch (error) {
      console.error('Error checking out guest:', error);
      setError('Failed to check out guest: ' + error.message);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading admin panel...</div>;
  }

  // Show access denied if user is not admin
  if (error && error.includes('Access denied')) {
    return (
      <div className="admin-panel">
        <div className="admin-header">
          <h1>Access Denied</h1>
        </div>
        <div className="admin-error-message">
          <p>You don't have permission to access the admin panel.</p>
          <p>Please contact an administrator if you believe this is an error.</p>
          <button onClick={() => window.location.href = '/'} className="admin-btn">
            Return to Home
          </button>
        </div>
      </div>
    );
  }
  const renderOverview = () => {
    // Calculate room statistics
    const totalRoomTypes = rooms.length;
    const totalRoomUnits = rooms.reduce((sum, room) => sum + (room.totalRooms || 0), 0);
    const confirmedBookings = bookings.filter(booking => 
      booking.status === 'CONFIRMED' || booking.status === 'CHECKED_IN'
    ).length;
    const availableRoomUnits = totalRoomUnits - confirmedBookings;
    
    return (
      <div className="overview-section">
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Room Types</h3>
            <p className="stat-number">{totalRoomTypes}</p>
            <p className="stat-description">Main room categories</p>
          </div>
          <div className="stat-card">
            <h3>Total Room Units</h3>
            <p className="stat-number">{totalRoomUnits}</p>
            <p className="stat-description">{totalRoomTypes} room types</p>
          </div>
          <div className="stat-card">
            <h3>Available Units</h3>
            <p className="stat-number">{availableRoomUnits}</p>
            <p className="stat-description">{totalRoomUnits} - {confirmedBookings} booked</p>
          </div>
          <div className="stat-card">
            <h3>Total Bookings</h3>
            <p className="stat-number">{bookings.length}</p>
            <p className="stat-description">All time bookings</p>
          </div>
          <div className="stat-card">
            <h3>Confirmed Bookings</h3>
            <p className="stat-number">{confirmedBookings}</p>
            <p className="stat-description">Active reservations</p>
          </div>
          <div className="stat-card">
            <h3>Total Users</h3>
            <p className="stat-number">{users.length}</p>
            <p className="stat-description">Registered users</p>
          </div>
          <div className="stat-card">
            <h3>Facilities</h3>
            <p className="stat-number">{facilities.length}</p>
            <p className="stat-description">Available amenities</p>
          </div>
        </div>
        
        <div className="refresh-section">
          <div className="section-header">
            <h3>Room Availability Management</h3>
            <button 
              onClick={handleRefreshRoomAvailability} 
              className="add-btn refresh-btn"
              disabled={loading}
            >
              {loading ? 'Refreshing...' : '🔄 Refresh Room Availability'}
            </button>
          </div>
          <p className="refresh-description">
            Click to automatically check out expired bookings and update room availability. 
            This will find bookings where checkout date has passed and mark them as checked out.
          </p>
        </div>
        
        <div className="recent-activity">
          <h3>Room Type Summary</h3>
          <div className="room-summary-table">
            <table className="summary-table">
              <thead>
                <tr>
                  <th>Room Type</th>
                  <th>Total Units</th>
                  <th>Available Units To Sell</th>
                  <th>Price per Night</th>
                  <th>Max Guests</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map(room => {
                  const roomBookings = bookings.filter(booking => 
                    booking.roomId === room.id && 
                    (booking.status === 'CONFIRMED' || booking.status === 'CHECKED_IN')
                  ).length;
                  const availableUnits = (room.totalRooms || 0) - roomBookings;
                  
                  return (
                    <tr key={room.id}>
                      <td><strong>{room.name}</strong></td>
                      <td>{room.totalRooms || 'N/A'}</td>
                      <td className={availableUnits > 0 ? 'available' : 'fully-booked'}>
                        {availableUnits >= 0 ? availableUnits : 'N/A'}
                      </td>
                      <td>₹{room.price}</td>
                      <td>{room.maxGuests}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderRooms = () => (
    <div className="rooms-section">
      <div className="section-header">
        <h2>Rooms Management</h2>
        <button onClick={handleAddRoom} className="add-btn">Add New Room</button>
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')}>Room Type Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('type')}>Type {sortBy === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('size')}>Size {sortBy === 'size' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('price')}>Price {sortBy === 'price' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('maxGuests')}>Max Guests {sortBy === 'maxGuests' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('totalRooms')}>Total Rooms {sortBy === 'totalRooms' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('availableRooms')}>Available {sortBy === 'availableRooms' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('available')}>Status {sortBy === 'available' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortData(rooms, sortBy).map(room => (
              <tr key={room.id}>
                <td>{cleanRoomName(room.name)}</td>
                <td>{room.type}</td>
                <td>{room.size}</td>
                <td>₹{room.price}</td>
                <td>{room.maxGuests}</td>
                <td>{room.totalRooms || 'N/A'}</td>
                <td>{room.availableRooms || 'N/A'}</td>
                <td>{room.available ? 'Active' : 'Inactive'}</td>
                <td>
                  <button onClick={() => handleEditRoom(room)} className="edit-btn">Edit</button>
                  <button onClick={() => handleDeleteRoom(room.id)} className="delete-btn">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderBookings = () => (
    <div className="bookings-section">
      <div className="section-header">
        <h2>Bookings Management</h2>
        <div className="booking-actions">
          <select
            value={bookingStatusFilter}
            onChange={(e) => setBookingStatusFilter(e.target.value)}
            className="booking-filter-select"
            title="Filter bookings by status"
          >
            <option value="ALL">All Statuses</option>
            <option value="PAYMENT_FAILED">Payment Failed</option>
            <option value="PENDING">Pending</option>
            <option value="CONFIRMED">Confirmed</option>
            <option value="CHECKED_IN">Checked In</option>
            <option value="CHECKED_OUT">Checked Out</option>
            <option value="CANCELLED">Cancelled</option>
            <option value="COMPLETED">Completed</option>
          </select>
          <button onClick={handleCreateBooking} className="add-btn">Create New Booking</button>
          <button onClick={handleRefreshBookings} className="refresh-btn">🔄 Refresh</button>
        </div>
      </div>
      
      
      
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')} className="sortable">
                ID {sortBy === 'id' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('userName')} className="sortable">
                User {sortBy === 'userName' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('roomName')} className="sortable">
                Room {sortBy === 'roomName' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('checkInDate')} className="sortable">
                Check-in {sortBy === 'checkInDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('checkOutDate')} className="sortable">
                Check-out {sortBy === 'checkOutDate' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('guests')} className="sortable">
                Guests {sortBy === 'guests' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('totalPrice')} className="sortable">
                Total Price {sortBy === 'totalPrice' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('status')} className="sortable">
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSortedBookings.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  {bookingStatusFilter === 'ALL'
                    ? 'No bookings found. Bookings endpoint may not be available.'
                    : `No bookings found for status: ${bookingStatusFilter}`}
                </td>
              </tr>
            ) : (
              filteredSortedBookings.map(booking => {
                // Use direct fields from backend, with fallbacks
                const userName = cleanDisplayText(booking.userName || booking.userEmail, 'Unknown User');
                const roomName = cleanDisplayText(booking.roomName, 'Unknown Room');
                const statusColor = getBookingStatusColor(booking.status);
                const isUpcoming = new Date(booking.checkInDate) > new Date();
                const isActive = booking.status === 'CONFIRMED' || booking.status === 'CHECKED_IN';
                
                return (
                  <tr key={booking.id} className={`booking-row booking-${booking.status?.toLowerCase()}`}>
                    <td><strong>#{booking.id}</strong></td>
                    <td>
                      <div className="user-info">
                        <div>{userName}</div>
                        <small>{booking.userEmail}</small>
                      </div>
                    </td>
                    <td>{roomName}</td>
                    <td>{new Date(booking.checkInDate).toLocaleDateString()}</td>
                    <td>{new Date(booking.checkOutDate).toLocaleDateString()}</td>
                    <td>{booking.guests || 'N/A'}</td>
                    <td><strong>₹{formatCurrency(booking.totalPrice)}</strong></td>
                    <td>
                      <span className={`status-badge status-${booking.status?.toLowerCase()}`} style={{color: statusColor}}>
                        {booking.status}
                      </span>
                    </td>
                    <td>                      <div className="booking-actions">
                        <button 
                          onClick={() => handleViewBooking(booking)} 
                          className="view-btn"
                          title="View Details"
                        >
                          👁️
                        </button>
                        
                        {/* Status Update Dropdown */}
                        <select
                          value={booking.status}
                          onChange={(e) => handleUpdateBookingStatus(booking, e.target.value)}
                          className="status-select"
                          title="Update Status"
                        >
                          <option value="PENDING">PENDING</option>
                          <option value="PAYMENT_FAILED">PAYMENT_FAILED</option>
                          <option value="CONFIRMED">CONFIRMED</option>
                          <option value="CANCELLED">CANCELLED</option>
                          <option value="COMPLETED">COMPLETED</option>
                        </select>
                        
                        {isActive && (
                          <button 
                            onClick={() => handleEditBooking(booking)} 
                            className="edit-btn"
                            title="Edit Booking"
                          >
                            ✏️
                          </button>
                        )}
                        {isUpcoming && isActive && (
                          <button 
                            onClick={() => handleCancelBooking(booking)} 
                            className="cancel-btn"
                            title="Cancel Booking"
                          >
                            ❌
                          </button>
                        )}
                        {booking.status === 'CONFIRMED' && isUpcoming && (
                          <button 
                            onClick={() => handleCheckInBooking(booking)} 
                            className="checkin-btn"
                            title="Check In"
                          >
                            🏨
                          </button>
                        )}
                        {booking.status === 'CHECKED_IN' && (
                          <button 
                            onClick={() => handleCheckOutBooking(booking)} 
                            className="checkout-btn"
                            title="Check Out"
                          >
                            🚪
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const handleConfirmConsolidatedBooking = async (groupKey) => {
    if (!window.confirm('Are you sure you want to confirm this consolidated booking? This will send a confirmation message to the customer.')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/admin/bookings/consolidated/${encodeURIComponent(groupKey)}/confirm`, {
        method: 'POST'
      });

      if (response.ok) {
        setSuccessMessage('Consolidated booking confirmed successfully! Customer has been notified.');
        // Refresh consolidated bookings
        const consolidatedRes = await fetchWithAuth('/api/admin/bookings/consolidated');
        if (consolidatedRes.ok) {
          const consolidatedData = await consolidatedRes.json();
          setConsolidatedBookings(Array.isArray(consolidatedData) ? consolidatedData : []);
        }
      } else {
        setError('Failed to confirm consolidated booking. Please try again.');
      }
    } catch (error) {
      console.error('Error confirming consolidated booking:', error);
      setError('Failed to confirm consolidated booking. Please try again.');
    }
  };

  const handleRefreshRoomAvailability = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      const response = await fetchWithAuth('/api/admin/rooms/refresh-availability', {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(
          `Room availability refreshed! Found ${result.expiredBookingsFound} expired bookings, ` +
          `updated ${result.bookingsUpdated} bookings. Room availability updated.`
        );
        
        // Refresh all data to show updated room availability
        fetchAdminData();
      } else {
        const errorText = await response.text();
        setError(errorText || 'Failed to refresh room availability');
      }
    } catch (error) {
      console.error('Error refreshing room availability:', error);
      setError('Failed to refresh room availability: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderConsolidatedBookings = () => (
    <div className="consolidated-bookings-section">
      <div className="section-header">
        <h2>Consolidated Bookings Management</h2>
        <p>Group bookings by customer and date for easy management and confirmation</p>
        <div className="admin-header-actions">
          <button onClick={fetchAdminData} className="refresh-btn" title="Refresh consolidated bookings">
            Refresh
          </button>
        </div>
      </div>
      
      <div className="admin-table-container">
        {consolidatedBookings.length === 0 ? (
          <div className="no-data">
            <p>No consolidated bookings found.</p>
          </div>
        ) : (
          <div className="consolidated-bookings-list">
            {consolidatedBookings.map((group, index) => (
              <div key={index} className="consolidated-booking-group">
                <div className="group-header">
                  <h3>
                    Customer: {group.guestName || group.userEmail} ({group.guestPhone || 'No phone'})
                  </h3>
                  <div className="group-info">
                    <span>Email: {group.guestEmail || group.userEmail}</span>
                    <span>Check-in: {group.checkInDate}</span>
                    <span>Check-out: {group.checkOutDate}</span>
                    <span>Total Bookings: {group.bookings.length}</span>
                    <span>Total Amount: ₹{formatCurrency(group.totalAmount)}</span>
                  </div>
                  <button 
                    onClick={() => handleConfirmConsolidatedBooking(group.groupKey)}
                    className="confirm-btn"
                    disabled={group.allConfirmed}
                  >
                    {group.allConfirmed ? 'Already Confirmed' : 'Confirm Booking'}
                  </button>
                </div>
                
                <div className="group-bookings">
                  <div className="admin-table-container">
                    <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Booking ID</th>
                        <th>Room Name</th>
                        <th>Occupancy</th>
                        <th>Guests</th>
                        <th>Status</th>
                        <th>Confirmed</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.bookings.map((booking) => (
                        <tr key={booking.id}>
                          <td>{booking.id}</td>
                          <td>{booking.roomName || 'Unknown Room'}</td>
                          <td>{booking.occupancyType || 'N/A'}</td>
                          <td>{booking.guests || 'N/A'}</td>
                          <td>
                            <select 
                              value={booking.status} 
                              onChange={(e) => handleUpdateBookingStatus(booking, e.target.value)}
                              className="status-select"
                            >
                              <option value="PENDING">PENDING</option>
                              <option value="CONFIRMED">CONFIRMED</option>
                              <option value="CANCELLED">CANCELLED</option>
                              <option value="COMPLETED">COMPLETED</option>
                            </select>
                          </td>
                          <td>
                            <span className={`confirmation-badge ${booking.status === 'CONFIRMED' ? 'confirmed' : 'pending'}`}>
                              {booking.status === 'CONFIRMED' ? 'Yes' : 'Pending'}
                            </span>
                          </td>
                          <td>
                            <button 
                              onClick={() => setSelectedBooking(booking)} 
                              className="edit-btn"
                              title="View Details"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className="group-total-row">
                        <td colSpan="6" className="total-label"><strong>Total Amount:</strong></td>
                        <td className="total-amount"><strong>₹{formatCurrency(group.totalAmount)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderFacilities = () => (
    <div className="facilities-section">
      <div className="section-header">
        <h2>Facilities Management</h2>
        <button onClick={handleAddFacility} className="add-btn">Add New Facility</button>
      </div>
      
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Usage Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {facilities.map(facility => (
              <tr key={facility.id}>
                <td>{facility.name}</td>
                <td>{facility.description}</td>
                <td>{facility.usageCount || 0}</td>
                <td>
                  <button onClick={() => handleEditFacility(facility)} className="edit-btn">Edit</button>
                  <button onClick={() => handleDeleteFacility(facility.id)} className="delete-btn">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderPricing = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = new Date().toISOString().split('T')[0];

    const monthNames = ['January','February','March','April','May','June',
                        'July','August','September','October','November','December'];

    const fmt2 = n => String(n).padStart(2, '0');
    const toDateStr = d => `${year}-${fmt2(month + 1)}-${fmt2(d)}`;

    const getActivePricingForDateRoom = (roomId, dateStr) =>
      roomId === 'ALL'
        ? getAllActivePricingForDate(dateStr)[0] || null
        : getLatestPricingRuleForRoomDate(roomId, dateStr);

    const getAllActivePricingForDate = dateStr =>
      pricingRules
        .filter(r => dateStr >= r.startDate && dateStr <= r.endDate)
        .sort((left, right) => {
          const leftCreatedAt = left.createdAt ? new Date(left.createdAt).getTime() : 0;
          const rightCreatedAt = right.createdAt ? new Date(right.createdAt).getTime() : 0;

          if (leftCreatedAt !== rightCreatedAt) {
            return rightCreatedAt - leftCreatedAt;
          }

          const leftId = Number(left.id) || 0;
          const rightId = Number(right.id) || 0;
          return rightId - leftId;
        });

    const getBookingsForDateRoom = (roomId, dateStr) =>
      bookings.filter(b =>
        (roomId === 'ALL' || String(b.roomId) === String(roomId)) &&
        b.status !== 'CANCELLED' &&
        b.status !== 'PAYMENT_FAILED' &&
        dateStr >= b.checkInDate &&
        dateStr < b.checkOutDate
      );

    // Build calendar cells
    const calendarDays = [];
    for (let i = 0; i < firstDayOfWeek; i++) calendarDays.push(null);
    for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

    const handleDayClick = (dateStr) => {
      if (selectedCalendarDate === dateStr) {
        setSelectedCalendarDate(null);
      } else {
        setSelectedCalendarDate(dateStr);
        setBulkRangeStart(dateStr);
        setBulkRangeEnd(dateStr);
        setBulkUpdateForm({
          notes: '',
          roomRows: buildBulkRowsForDate(dateStr)
        });
      }
    };

    const renderDayDetail = (dateStr) => {
      const dayBookings = getBookingsForDateRoom('ALL', dateStr);
      const visibleRooms = calendarRoomFilter === 'ALL'
        ? rooms
        : rooms.filter(room => String(room.id) === String(calendarRoomFilter));

      const setVisibleRoomsEnabled = (enabled) => {
        setBulkUpdateForm(prev => {
          const nextRows = { ...(prev.roomRows || {}) };
          visibleRooms.forEach(room => {
            const key = String(room.id);
            const existing = nextRows[key] || getRoomDefaultValuesForDate(room, dateStr);
            nextRows[key] = {
              ...existing,
              enabled
            };
          });
          return {
            ...prev,
            roomRows: nextRows
          };
        });
      };

      return (
        <div className="cal-detail-panel">
          <div className="cal-detail-header">
            <div>
              <div className="cal-detail-title">
                {new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="cal-detail-subtitle">
                {dayBookings.length} active booking{dayBookings.length !== 1 ? 's' : ''} across all rooms
              </div>
            </div>
            <button className="cal-detail-close" onClick={() => setSelectedCalendarDate(null)}>×</button>
          </div>

          {/* Per-room summary cards */}
          <div className="cal-room-grid">
            {rooms.map(room => {
              const roomBookings = getBookingsForDateRoom(String(room.id), dateStr);
              const activePricing = getActivePricingForDateRoom(String(room.id), dateStr);
              const effectiveInventory = activePricing?.availableRoomsOverride ?? room.totalRooms ?? '–';
              const singlePrice = activePricing ? activePricing.singleOccupancyPrice : room.singleOccupancyPrice;
              const doublePrice = activePricing ? activePricing.doubleOccupancyPrice : room.doubleOccupancyPrice;

              return (
                <div key={room.id} className={`cal-room-card ${activePricing ? 'cal-room-card-custom' : ''}`}>
                  <div className="cal-room-card-title">{room.name}</div>
                  <div className="cal-room-stat">
                    <span className="cal-room-stat-label">Single</span>
                    <span className={activePricing ? 'cal-room-stat-custom' : ''}>
                      ₹{formatCurrency(singlePrice)}
                      {activePricing && <span className="cal-custom-tag"> custom</span>}
                    </span>
                  </div>
                  <div className="cal-room-stat">
                    <span className="cal-room-stat-label">Double</span>
                    <span className={activePricing ? 'cal-room-stat-custom' : ''}>
                      ₹{formatCurrency(doublePrice)}
                    </span>
                  </div>
                  <div className="cal-room-stat">
                    <span className="cal-room-stat-label">Inventory</span>
                    <span className={activePricing?.availableRoomsOverride != null ? 'cal-room-stat-custom' : ''}>
                      {effectiveInventory}
                      {activePricing?.availableRoomsOverride != null && <span className="cal-custom-tag"> OTA block</span>}
                    </span>
                  </div>
                  <div className="cal-room-stat">
                    <span className="cal-room-stat-label">Bookings</span>
                    <span style={{ fontWeight: roomBookings.length > 0 ? 700 : 400 }}>
                      {roomBookings.length}
                    </span>
                  </div>
                  {activePricing && (
                    <div className="cal-room-card-actions">
                      <button
                        className="edit-btn"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={() => handleEditPricing(activePricing)}
                      >Edit Rule</button>
                      <button
                        className="delete-btn"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={() => handleDeletePricing(activePricing.id)}
                      >Delete</button>
                    </div>
                  )}
                  {!activePricing && (
                    <div className="cal-room-card-actions">
                      <button
                        className="add-btn"
                        style={{ fontSize: '11px', padding: '3px 8px' }}
                        onClick={() => {
                          setPricingForm({
                            roomId: String(room.id),
                            startDate: dateStr,
                            endDate: dateStr,
                            singleOccupancyPrice: String(room.singleOccupancyPrice || ''),
                            doubleOccupancyPrice: String(room.doubleOccupancyPrice || ''),
                            availableRoomsOverride: '',
                            notes: ''
                          });
                          setEditingPricing(null);
                          setIsPricingModalOpen(true);
                        }}
                      >+ Add Rule</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bulk Update Form */}
          <div className="cal-bulk-section">
            <div className="cal-bulk-title">Bulk Update Pricing & Inventory</div>
            <form onSubmit={handleBulkSavePricing}>
              {/* Date range */}
              <div className="cal-bulk-form-row">
                <div className="form-group">
                  <label>From Date</label>
                  <input
                    type="date"
                    value={bulkRangeStart}
                    onChange={e => setBulkRangeStart(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>To Date</label>
                  <input
                    type="date"
                    value={bulkRangeEnd}
                    min={bulkRangeStart}
                    onChange={e => setBulkRangeEnd(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Room-wise rows */}
              <div className="form-group">
                <label>Room-wise Price & Inventory</label>
                <small>
                  Formula: Final available = Total rooms - Website bookings - OTA bookings (this field). Leave blank or 0 when there are no OTA bookings.
                </small>
                <div className="cal-bulk-row-actions">
                  <button
                    type="button"
                    className="cal-bulk-action-btn"
                    onClick={() => setVisibleRoomsEnabled(true)}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="cal-bulk-action-btn"
                    onClick={() => setVisibleRoomsEnabled(false)}
                  >
                    Deselect All
                  </button>
                </div>
                <div className="cal-bulk-grid-header">
                  <span>Update</span>
                  <span>Room Type</span>
                  <span>Single (₹)</span>
                  <span>Double (₹)</span>
                  <span>OTA bookings</span>
                </div>
                {visibleRooms.map(room => {
                  const row = bulkUpdateForm.roomRows?.[String(room.id)] || getRoomDefaultValuesForDate(room, dateStr);
                  return (
                    <div className="cal-bulk-grid-row" key={room.id}>
                      <label className="cal-bulk-enable-label">
                        <input
                          type="checkbox"
                          checked={Boolean(row.enabled)}
                          onChange={e => setBulkUpdateForm(prev => ({
                            ...prev,
                            roomRows: {
                              ...prev.roomRows,
                              [String(room.id)]: {
                                ...row,
                                enabled: e.target.checked
                              }
                            }
                          }))}
                        />
                      </label>
                      <div className="cal-bulk-room-name">{room.name}</div>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.singleOccupancyPrice}
                        onChange={e => setBulkUpdateForm(prev => ({
                          ...prev,
                          roomRows: {
                            ...prev.roomRows,
                            [String(room.id)]: {
                              ...row,
                              singleOccupancyPrice: e.target.value
                            }
                          }
                        }))}
                        onWheel={e => e.target.blur()}
                        disabled={!row.enabled}
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.doubleOccupancyPrice}
                        onChange={e => setBulkUpdateForm(prev => ({
                          ...prev,
                          roomRows: {
                            ...prev.roomRows,
                            [String(room.id)]: {
                              ...row,
                              doubleOccupancyPrice: e.target.value
                            }
                          }
                        }))}
                        onWheel={e => e.target.blur()}
                        disabled={!row.enabled}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="Blank = 0 OTA bookings"
                        value={row.availableRoomsOverride}
                        onChange={e => setBulkUpdateForm(prev => ({
                          ...prev,
                          roomRows: {
                            ...prev.roomRows,
                            [String(room.id)]: {
                              ...row,
                              availableRoomsOverride: e.target.value
                            }
                          }
                        }))}
                        onWheel={e => e.target.blur()}
                        disabled={!row.enabled}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="form-group">
                <label>Notes (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g., Festival weekend, OTA block"
                  value={bulkUpdateForm.notes}
                  onChange={e => setBulkUpdateForm(p => ({ ...p, notes: e.target.value }))}
                />
              </div>

              <button type="submit" className="cal-bulk-save-btn" disabled={isBulkSaving}>
                {isBulkSaving ? 'Saving...' : 'Save Room-wise Updates'}
              </button>
            </form>
          </div>
        </div>
      );
    };

    return (
      <div className="pricing-section pricing-calendar-section">
        {/* Header */}
        <div className="section-header">
          <h2>Pricing and Inventory</h2>
          <div className="pricing-header-actions">
            {/*<select
              value={calendarRoomFilter}
              onChange={e => setCalendarRoomFilter(e.target.value)}
              className="room-filter-select"
            >
               <option value="ALL">All Rooms</option>
              {rooms.map(r => (
                <option key={r.id} value={String(r.id)}>{r.name}</option>
              ))}
            </select>
            <button onClick={handleAddPricing} className="add-btn">+ Add Pricing Rule</button> */}
          </div>
        </div>

        {error && <div className="pricing-inline-message pricing-inline-error">{error}</div>}
        {successMessage && <div className="pricing-inline-message pricing-inline-success">{successMessage}</div>}

        {/* Month navigation */}
        <div className="calendar-nav">
          <button className="cal-nav-btn" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))}>‹</button>
          <h3 className="calendar-month-title">{monthNames[month]} {year}</h3>
          <button className="cal-nav-btn" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))}>›</button>
          <button className="cal-today-btn" onClick={() => { setCalendarMonth(new Date()); setSelectedCalendarDate(null); }}>
            Today
          </button>
        </div>

        {/* Calendar grid */}
        <div className="pricing-calendar">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="cal-header-cell">{d}</div>
          ))}

          {calendarDays.map((day, idx) => {
            if (!day) return <div key={`e${idx}`} className="cal-cell cal-cell-empty" />;

            const dateStr = toDateStr(day);
            const isToday = dateStr === todayStr;
            const isSelected = selectedCalendarDate === dateStr;
            const isPast = dateStr < todayStr;

            const dayBookings = getBookingsForDateRoom(calendarRoomFilter, dateStr);
            const activePricing = calendarRoomFilter !== 'ALL'
              ? getActivePricingForDateRoom(calendarRoomFilter, dateStr)
              : null;
            const allRulesForDay = calendarRoomFilter === 'ALL'
              ? getAllActivePricingForDate(dateStr)
              : [];
            const hasCustomPricing = activePricing || allRulesForDay.length > 0;

            return (
              <div
                key={dateStr}
                className={[
                  'cal-cell',
                  isToday ? 'cal-cell-today' : '',
                  isSelected ? 'cal-cell-selected' : '',
                  isPast ? 'cal-cell-past' : '',
                  hasCustomPricing ? 'cal-cell-has-pricing' : ''
                ].filter(Boolean).join(' ')}
                onClick={() => handleDayClick(dateStr)}
              >
                <div className="cal-date-num">{day}</div>
                {dayBookings.length > 0 && (
                  <div className="cal-bookings-badge">
                    {dayBookings.length} bkg{dayBookings.length > 1 ? 's' : ''}
                  </div>
                )}
                {activePricing && (
                  <div className="cal-pricing-badge">
                    ₹{formatCurrency(activePricing.singleOccupancyPrice)}
                    {activePricing.availableRoomsOverride != null && (
                      <span className="cal-inv-badge"> · {activePricing.availableRoomsOverride}rm</span>
                    )}
                  </div>
                )}
                {calendarRoomFilter === 'ALL' && allRulesForDay.length > 0 && (
                  <div className="cal-pricing-badge">
                    {allRulesForDay.length} rule{allRulesForDay.length > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Day detail panel */}
        {selectedCalendarDate && renderDayDetail(selectedCalendarDate)}

        {/* Existing rules table at bottom */}
        {/* <div className="cal-rules-table-section">
          <h3 className="cal-rules-title">All Pricing Rules</h3>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Room</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Single Price</th>
                  <th>Double Price</th>
                  <th>OTA bookings</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pricingRules.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                      No pricing rules yet. Click a date on the calendar to add one.
                    </td>
                  </tr>
                ) : (
                  pricingRules.map(pricing => (
                    <tr key={pricing.id}>
                      <td>{pricing.roomName}</td>
                      <td>{pricing.startDate}</td>
                      <td>{pricing.endDate}</td>
                      <td>₹{formatCurrency(pricing.singleOccupancyPrice)}</td>
                      <td>₹{formatCurrency(pricing.doubleOccupancyPrice)}</td>
                      <td>{pricing.availableRoomsOverride ?? 'Default (room total)'}</td>
                      <td>{pricing.notes || '–'}</td>
                      <td>
                        <button onClick={() => handleEditPricing(pricing)} className="edit-btn">Edit</button>
                        <button onClick={() => handleDeletePricing(pricing.id)} className="delete-btn">Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div> */}
      </div>
    );
  };

  const renderUsers = () => (
    <div className="users-section">
      <div className="section-header">
        <h2>Users Management</h2>
      </div>
      
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">No users found. Users endpoint may not be available.</td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.username || user.name || '-'}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    <button onClick={() => handleEditUser(user)} className="edit-btn">Edit</button>
                    <button onClick={() => handleDeleteUser(user.id)} className="delete-btn">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <div className="admin-header-actions">
          <button 
            onClick={() => {
              setSuccessMessage('Refreshing data...');
              fetchAdminData();
            }} 
            className="refresh-btn"
            title="Refresh Data"
          >
            🔄 Refresh
          </button>
          <button onClick={() => {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            window.location.href = '/';
          }} className="logout-btn">Logout</button>
        </div>
      </div>

      {error && <div className="admin-error-message">{error}</div>}
      {successMessage && <div className="admin-success-message">{successMessage}</div>}

      {/* Navigation Tabs */}
      <div className="admin-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'rooms' ? 'active' : ''}`}
          onClick={() => setActiveTab('rooms')}
        >
          Rooms
        </button>
        <button 
          className={`nav-tab ${activeTab === 'bookings' ? 'active' : ''}`}
          onClick={() => setActiveTab('bookings')}
        >
          Bookings
        </button>
        <button 
          className={`nav-tab ${activeTab === 'consolidated' ? 'active' : ''}`}
          onClick={() => setActiveTab('consolidated')}
        >
          Consolidated Bookings
        </button>
        <button 
          className={`nav-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`nav-tab ${activeTab === 'facilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('facilities')}
        >
          Facilities
        </button>
        <button 
          className={`nav-tab ${activeTab === 'pricing' ? 'active' : ''}`}
          onClick={() => setActiveTab('pricing')}
        >
          Pricing and Inventory
        </button>
      </div>

      {/* Tab Content */}
      <div className="admin-content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'rooms' && renderRooms()}
        {activeTab === 'bookings' && renderBookings()}
        {activeTab === 'consolidated' && renderConsolidatedBookings()}
        {activeTab === 'users' && renderUsers()}
        {activeTab === 'facilities' && renderFacilities()}
        {activeTab === 'pricing' && renderPricing()}
      </div>      {/* Room Modal */}
      {isRoomModalOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingRoom ? 'Edit Room' : 'Add New Room'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>            <form onSubmit={handleSaveRoom} className="room-form">
              {/* Basic Information Section */}
              <div className="form-section">
                <h4 className="section-title">Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Room Type Name *</label>
                    <input
                      type="text"
                      value={roomForm.name}
                      onChange={(e) => setRoomForm({...roomForm, name: e.target.value})}
                      required
                      placeholder="e.g., Deluxe AC Room"
                    />
                  </div>
                  <div className="form-group">
                    <label>Type *</label>
                    <select
                      value={roomForm.type}
                      onChange={(e) => setRoomForm({...roomForm, type: e.target.value})}
                      required
                    >
                      <option value="">Select Type</option>
                      <option value="Standard">Standard</option>
                      <option value="Deluxe">Deluxe</option>
                      <option value="Suite">Suite</option>
                      <option value="Premium">Premium</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Size *</label>
                    <input
                      type="text"
                      value={roomForm.size}
                      onChange={(e) => setRoomForm({...roomForm, size: e.target.value})}
                      required
                      placeholder="e.g., 300 sq ft"
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Guests *</label>
                    <input
                      type="number"
                      value={roomForm.maxGuests}
                      onChange={(e) => setRoomForm({...roomForm, maxGuests: e.target.value})}
                      onWheel={e => e.target.blur()}
                      required
                      min="1"
                      placeholder="e.g., 4"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={roomForm.description}
                    onChange={(e) => setRoomForm({...roomForm, description: e.target.value})}
                    rows="3"
                    placeholder="Room description..."
                  />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="form-section">
                <h4 className="section-title">Pricing Configuration</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Base Price per Night *</label>
                    <input
                      type="number"
                      value={roomForm.price}
                      onChange={(e) => setRoomForm({...roomForm, price: e.target.value})}
                      onWheel={e => e.target.blur()}
                      required
                      min="0"
                      step="0.01"
                      placeholder="e.g., 2500"
                    />
                    <small>Default pricing for the room</small>
                  </div>
                  <div className="form-group">
                    <label>Extra Bed Price</label>
                    <input
                      type="number"
                      value={roomForm.extraBedPrice}
                      onChange={(e) => setRoomForm({...roomForm, extraBedPrice: e.target.value})}
                      onWheel={e => e.target.blur()}
                      min="0"
                      step="0.01"
                      placeholder="e.g., 500"
                    />
                    <small>Additional charge for extra bed per night</small>
                  </div>
                </div>
                <div className="pricing-subsection">
                  <h5 className="subsection-title">Occupancy-Based Pricing (Optional)</h5>
                  <p className="subsection-description">Set different prices based on occupancy. Leave empty to use base price.</p>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Single Occupancy Price</label>
                      <input
                        type="number"
                        value={roomForm.singleOccupancyPrice}
                        onChange={(e) => setRoomForm({...roomForm, singleOccupancyPrice: e.target.value})}
                        onWheel={e => e.target.blur()}
                        min="0"
                        step="0.01"
                        placeholder="e.g., 2000"
                      />
                      <small>Price for single occupancy bookings</small>
                    </div>
                    <div className="form-group">
                      <label>Double Occupancy Price</label>
                      <input
                        type="number"
                        value={roomForm.doubleOccupancyPrice}
                        onChange={(e) => setRoomForm({...roomForm, doubleOccupancyPrice: e.target.value})}
                        onWheel={e => e.target.blur()}
                        min="0"
                        step="0.01"
                        placeholder="e.g., 2800"
                      />
                      <small>Price for double occupancy bookings</small>
                    </div>
                  </div>
                </div>
                <div className="pricing-subsection">
                  <h5 className="subsection-title">Breakfast Option</h5>
                  <div className="form-group">
                    <label>Breakfast Price per Person</label>
                    <input
                      type="number"
                      value={roomForm.breakfastPrice}
                      onChange={(e) => setRoomForm({...roomForm, breakfastPrice: e.target.value})}
                      onWheel={e => e.target.blur()}
                      min="0"
                      step="1"
                      placeholder="e.g., 250"
                    />
                    <small>Price per person per night for breakfast (in whole rupees). Leave empty to disable breakfast option.</small>
                  </div>
                </div>
              </div>

              {/* Availability Section */}
              <div className="form-section">
                <h4 className="section-title">Availability & Status</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Status</label>
                    <select
                      value={roomForm.available}
                      onChange={(e) => setRoomForm({...roomForm, available: e.target.value === 'true'})}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                    <small>Active rooms are available for booking</small>
                  </div>
                  <div className="form-group">
                    <label>Total Rooms</label>
                    <input
                      type="number"
                      value={roomForm.totalRooms}
                      onChange={(e) => setRoomForm({...roomForm, totalRooms: e.target.value})}
                      onWheel={e => e.target.blur()}
                      min="1"
                      placeholder="e.g., 10"
                    />
                    <small>Total number of rooms of this type</small>
                  </div>
                </div>
                <div className="form-group">
                  <label>Available Rooms</label>
                  <input
                    type="number"
                    value={roomForm.availableRooms}
                    onChange={(e) => setRoomForm({...roomForm, availableRooms: e.target.value})}
                    onWheel={e => e.target.blur()}
                    min="0"
                    placeholder="e.g., 8"
                  />
                  <small>Currently available rooms for booking</small>
                </div>
              </div>              {/* Facilities Section */}
              <div className="form-section">
                <h4 className="section-title">Room Facilities</h4>
                <div className="form-group">
                  <label>Available Facilities</label>
                  <div className="facilities-checkbox-group">
                    {facilities.map(facility => (
                      <label key={facility.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={roomForm.facilities.includes(facility.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoomForm({...roomForm, facilities: [...roomForm.facilities, facility.name]});
                            } else {
                              setRoomForm({...roomForm, facilities: roomForm.facilities.filter(f => f !== facility.name)});
                            }
                          }}
                        />
                        {facility.name}
                      </label>
                    ))}
                  </div>
                  <small>Select all facilities available in this room type</small>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="form-section">
                <h4 className="section-title">Room Image</h4>
                <div className="form-group">
                  <label>Upload Room Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="file-input"
                  />
                  {imagePreview && (
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" style={{maxWidth: '200px', maxHeight: '150px'}} />
                    </div>
                  )}
                  {uploadingImage && <p className="upload-status">Uploading image...</p>}
                  <small>Upload a high-quality image that represents this room type</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-btn">Cancel</button>
                <button type="submit" className="save-btn">{editingRoom ? 'Update' : 'Create'} Room</button>
              </div>
            </form>
          </div>
        </div>
      )}      {/* User Modal */}
      {isUserModalOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSaveUser} className="user-form">
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  required
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-btn">Cancel</button>
                <button type="submit" className="save-btn">Update User</button>
              </div>
            </form>
          </div>
        </div>
      )}      {/* Facility Modal */}
      {isFacilityModalOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingFacility ? 'Edit Facility' : 'Add New Facility'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSaveFacility} className="facility-form">
              <div className="form-group">
                <label>Facility Name *</label>
                <input
                  type="text"
                  value={facilityForm.name}
                  onChange={(e) => setFacilityForm({...facilityForm, name: e.target.value})}
                  required
                  placeholder="e.g., WiFi, Pool, Gym"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={facilityForm.description}
                  onChange={(e) => setFacilityForm({...facilityForm, description: e.target.value})}
                  rows="3"
                  placeholder="Facility description..."
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-btn">Cancel</button>
                <button type="submit" className="save-btn">{editingFacility ? 'Update' : 'Create'} Facility</button>
              </div>
            </form>
          </div>
        </div>
      )}      {/* Booking Creation Modal */}
      {isCreateBookingModalOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal booking-modal" onClick={(e) => e.stopPropagation()}>            <div className="modal-header">
              <h2>
                {editingBooking ? '✏️ Edit Booking' : '🎯 Create New Booking'}
              </h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div><form onSubmit={handleSaveBooking} className="booking-form">
              
              {/* User Selection */}
              <div className="form-section guest-info-section">
                <h3>Guest Information</h3>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      checked={bookingForm.createNewUser}
                      onChange={(e) => setBookingForm({...bookingForm, createNewUser: true})}
                    />
                    <span>Create New Guest</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      checked={!bookingForm.createNewUser}
                      onChange={(e) => setBookingForm({...bookingForm, createNewUser: false})}
                    />
                    <span>Use Existing User</span>
                  </label>
                </div>
                
                {bookingForm.createNewUser ? (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Guest Name *</label>
                        <input
                          type="text"
                          value={bookingForm.guestName}
                          onChange={(e) => setBookingForm({...bookingForm, guestName: e.target.value})}
                          required={bookingForm.createNewUser}
                          placeholder="Enter full name"
                        />
                      </div>
                      <div className="form-group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          value={bookingForm.guestEmail}
                          onChange={(e) => setBookingForm({...bookingForm, guestEmail: e.target.value})}
                          required={bookingForm.createNewUser}
                          placeholder="guest@example.com"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Phone Number (for SMS notifications)</label>
                      <input
                        type="tel"
                        value={bookingForm.guestPhone}
                        onChange={(e) => setBookingForm({...bookingForm, guestPhone: e.target.value})}
                        placeholder="+91 XXXXXXXXXX"
                      />
                    </div>
                  </>
                ) : (
                  <div className="form-group">
                    <label>Existing User Email *</label>
                    <input
                      type="email"
                      value={bookingForm.existingUserEmail}
                      onChange={(e) => setBookingForm({...bookingForm, existingUserEmail: e.target.value})}
                      required={!bookingForm.createNewUser}
                      placeholder="existing@example.com"
                    />
                  </div>
                )}
              </div>

              {/* Booking Details */}
              <div className="form-section booking-details-section">
                <h3>Booking Details</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Room Type *</label>
                    <select
                      value={bookingForm.roomId}
                        onChange={(e) => {
                          setRoomPriceAutoFilled(true);
                          setBookingForm({...bookingForm, roomId: e.target.value});
                        }}
                      required
                    >
                      <option value="">Select Room Type</option>
                      {rooms.filter(room => room.available).map(room => (
                        <option key={room.id} value={room.id}>
                          {room.name} - ₹{room.price}/night (Max {room.maxGuests} guests)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Number of Guests *</label>
                    <input
                      type="number"
                      value={bookingForm.guests}
                        onChange={(e) => {
                          setRoomPriceAutoFilled(true);
                          setBookingForm({...bookingForm, guests: parseInt(e.target.value) || 1});
                        }}
                      onWheel={e => e.target.blur()}
                      required
                      min="1"
                      max="10"
                      placeholder="1"
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in Date *</label>
                    <input
                      type="date"
                      value={bookingForm.checkInDate}
                        onChange={(e) => {
                          setRoomPriceAutoFilled(true);
                          setBookingForm({...bookingForm, checkInDate: e.target.value});
                        }}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="form-group">
                    <label>Check-out Date *</label>
                    <input
                      type="date"
                      value={bookingForm.checkOutDate}
                        onChange={(e) => {
                          setRoomPriceAutoFilled(true);
                          setBookingForm({...bookingForm, checkOutDate: e.target.value});
                        }}
                      required
                      min={bookingForm.checkInDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Room Price per Night (₹) *</label>
                    <input
                      type="number"
                      value={bookingForm.roomPrice}
                      onChange={(e) => {
                        setRoomPriceAutoFilled(false);
                        setBookingForm({...bookingForm, roomPrice: e.target.value});
                      }}
                      onWheel={e => e.target.blur()}
                      required
                      min="0"
                      step="0.01"
                      placeholder="Auto-filled from room/date pricing"
                    />
                    <small>
                      Auto-picked from selected room and date pricing. You can edit this value for this booking.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Total Room Amount (Auto)</label>
                    <input
                      type="text"
                      value={
                        getAdminBookingTotal() > 0
                          ? `₹${formatCurrency(getAdminBookingTotal())} (${calculateBookingNights(bookingForm.checkInDate, bookingForm.checkOutDate)} night${calculateBookingNights(bookingForm.checkInDate, bookingForm.checkOutDate) === 1 ? '' : 's'})`
                          : 'Select valid dates and room price'
                      }
                      readOnly
                    />
                    <small>
                      This total is sent while creating the booking.
                    </small>
                  </div>
                </div>

                {/* Additional Services */}
                <div className="form-row">
                  <div className="form-group">
                    <label>Extra Beds</label>
                    <input
                      type="number"
                      value={bookingForm.extraBeds}
                      onChange={(e) => setBookingForm({...bookingForm, extraBeds: parseInt(e.target.value) || 0})}
                      onWheel={e => e.target.blur()}
                      min="0"
                      max="3"
                      placeholder="0"
                    />
                    <small>Additional beds (₹700/bed/night)</small>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={bookingForm.includeBreakfast}
                        onChange={(e) => setBookingForm({...bookingForm, includeBreakfast: e.target.checked})}
                      />
                      <span>Include Breakfast (₹200/person/day)</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeModal} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingBooking ? '✏️ Update Booking' : '✨ Create Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}      {/* Booking Detail Modal */}
      {isBookingDetailModalOpen && selectedBooking && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal booking-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Details - #{selectedBooking.id}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>
            <div className="booking-details">
              <div className="detail-section">
                <h3>Guest Information</h3>
                <p><strong>Name:</strong> {selectedBooking.userName || 'N/A'}</p>
                <p><strong>Email:</strong> {selectedBooking.userEmail}</p>
                <p><strong>User ID:</strong> {selectedBooking.userId}</p>
              </div>
              
              <div className="detail-section">
                <h3>Booking Information</h3>
                <p><strong>Room:</strong> {selectedBooking.roomName || `Room ${selectedBooking.roomId}`}</p>
                <p><strong>Check-in:</strong> {new Date(selectedBooking.checkInDate).toLocaleDateString()}</p>
                <p><strong>Check-out:</strong> {new Date(selectedBooking.checkOutDate).toLocaleDateString()}</p>
                <p><strong>Guests:</strong> {selectedBooking.guests}</p>
                <p><strong>Total Price:</strong> ₹{formatCurrency(selectedBooking.totalPrice)}</p>
                <p><strong>Status:</strong> 
                  <span className={`status-badge status-${selectedBooking.status?.toLowerCase()}`}>
                    {selectedBooking.status}
                  </span>
                </p>
              </div>
              
              <div className="detail-actions">
                {selectedBooking.status === 'CONFIRMED' && (
                  <>
                    <button 
                      onClick={() => {
                        handleCheckInBooking(selectedBooking);
                        closeModal();
                      }} 
                      className="checkin-btn"
                    >
                      Check In Guest
                    </button>
                    <button 
                      onClick={() => {
                        handleCancelBooking(selectedBooking);
                        closeModal();
                      }} 
                      className="cancel-btn"
                    >
                      Cancel Booking
                    </button>
                  </>
                )}
                {selectedBooking.status === 'CHECKED_IN' && (
                  <button 
                    onClick={() => {
                      handleCheckOutBooking(selectedBooking);
                      closeModal();
                    }} 
                    className="checkout-btn"
                  >
                    Check Out Guest
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {isPricingModalOpen && (
        <div className="admin-modal-overlay" onClick={closeModal}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPricing ? 'Edit Pricing & Inventory Rule' : 'Add New Pricing & Inventory Rule'}</h2>
              <button onClick={closeModal} className="close-btn">×</button>
            </div>
            <form onSubmit={handleSavePricing} className="admin-form">
              <div className="form-group">
                <label>Room:</label>
                <select
                  value={pricingForm.roomId}
                  onChange={(e) => setPricingForm({...pricingForm, roomId: e.target.value})}
                  required
                >
                  <option value="">Select Room</option>
                  {rooms.map(room => (
                    <option key={room.id} value={room.id}>
                      {room.roomName || room.name} (Base: ₹{formatCurrency(room.singleOccupancyPrice)}/₹{formatCurrency(room.doubleOccupancyPrice)})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    value={pricingForm.startDate}
                    onChange={(e) => setPricingForm({...pricingForm, startDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>End Date:</label>
                  <input
                    type="date"
                    value={pricingForm.endDate}
                    onChange={(e) => setPricingForm({...pricingForm, endDate: e.target.value})}
                    min={pricingForm.startDate || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Single Occupancy Price (₹):</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingForm.singleOccupancyPrice}
                    onChange={(e) => setPricingForm({...pricingForm, singleOccupancyPrice: e.target.value})}
                    onWheel={e => e.target.blur()}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Double Occupancy Price (₹):</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingForm.doubleOccupancyPrice}
                    onChange={(e) => setPricingForm({...pricingForm, doubleOccupancyPrice: e.target.value})}
                    onWheel={e => e.target.blur()}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>OTA bookings for Date Range (Optional):</label>
                <input
                  type="number"
                  min="0"
                  value={pricingForm.availableRoomsOverride}
                  onChange={(e) => setPricingForm({...pricingForm, availableRoomsOverride: e.target.value})}
                  onWheel={e => e.target.blur()}
                  placeholder="Leave blank or 0 if no OTA bookings"
                />
                <small>Formula: Final available = Total rooms - Website bookings - OTA bookings (this field).</small>
              </div>
              
              <div className="form-group">
                <label>Notes (Optional):</label>
                <textarea
                  value={pricingForm.notes}
                  onChange={(e) => setPricingForm({...pricingForm, notes: e.target.value})}
                  rows="3"
                  placeholder="e.g., Festival pricing, Weekend rates, etc."
                />
              </div>
              
              <div className="modal-actions">
                <button type="submit" className="save-btn">
                  {editingPricing ? 'Update Pricing & Inventory' : 'Create Pricing & Inventory'}
                </button>
                <button type="button" onClick={closeModal} className="cancel-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
